import React, { useEffect, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { candidate_name: "", mobile: "", email: "", department_id: "", inquiry_details: "" };

export default function AdmissionInquiryModal({ open, onClose, slug, departments = [] }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setForm(EMPTY); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose, submitting]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const name = form.candidate_name.trim();
    if (!name) return toast.error("Please enter your name.");
    if (name.length > 100) return toast.error("Name must be 100 characters or fewer.");
    const mobile = form.mobile.replace(/\D/g, "");
    if (mobile.length !== 10) return toast.error("Mobile must be exactly 10 digits.");
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Please enter a valid email.");
    if (form.inquiry_details.length > 500) return toast.error("Inquiry details must be 500 characters or fewer.");
    try {
      setSubmitting(true);
      await api.post(`/public/admission-inquiry/${slug}`, {
        candidate_name: name, mobile, email: form.email.trim(),
        department_id: form.department_id || "", inquiry_details: form.inquiry_details.trim(),
      });
      toast.success("Inquiry submitted! Our admissions team will reach out shortly.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="nur-admission-inquiry-modal">
      <div className="absolute inset-0 bg-[#0A1F47]/75 backdrop-blur-sm" onClick={() => !submitting && onClose()} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="relative bg-gradient-to-r from-[#0F2A5F] via-[#1B3E83] to-[#0FB28A] text-white px-6 py-5 flex items-start justify-between overflow-hidden">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#7FE7CE] font-semibold">Take the next step</div>
            <h3 className="font-['Poppins',sans-serif] font-bold text-xl leading-tight mt-1">Talk to our Admissions Team</h3>
          </div>
          <button type="button" onClick={() => !submitting && onClose()} className="text-white/80 hover:text-white p-1" data-testid="nur-admission-inquiry-close" aria-label="Close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <Field label="Candidate Name *">
            <input type="text" maxLength={100} value={form.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} className={inputCls} placeholder="Full name" autoFocus data-testid="nur-ai-name" />
          </Field>
          <Field label="Mobile Number *">
            <input type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10} value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} className={inputCls} placeholder="10-digit mobile number" data-testid="nur-ai-mobile" />
          </Field>
          <Field label="Email ID (Optional)">
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@example.com" data-testid="nur-ai-email" />
          </Field>
          <Field label="Programme Interested In">
            <select value={form.department_id} onChange={(e) => set("department_id", e.target.value)} className={inputCls} data-testid="nur-ai-department">
              <option value="">— Select programme —</option>
              {departments.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((d) => (
                <option key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</option>
              ))}
            </select>
          </Field>
          <Field label="Inquiry Details">
            <textarea rows={4} maxLength={500} value={form.inquiry_details} onChange={(e) => set("inquiry_details", e.target.value)} className={`${inputCls} resize-y min-h-[100px]`} placeholder="Tell us about your interest, preferred programme, etc." data-testid="nur-ai-details" />
          </Field>
          <div className="pt-2 flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm rounded-lg text-[#475569] hover:bg-[#F4F9FF] disabled:opacity-50" data-testid="nur-ai-cancel">Cancel</button>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0FB28A] to-[#0E916F] hover:from-[#0E916F] hover:to-[#0a7757] text-white text-sm font-semibold px-5 py-2 rounded-lg shadow-sm disabled:opacity-60" data-testid="nur-ai-submit">
              {submitting ? (<><Loader2 size={14} className="animate-spin" /> Sending…</>) : (<><Send size={14} /> Submit Inquiry</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0FB28A] focus:border-[#0FB28A]";
function Field({ label, children }) { return (<label className="block"><div className="text-xs font-semibold uppercase tracking-wider text-[#0F2A5F] mb-1.5">{label}</div>{children}</label>); }
